'use client';

import { DialogDescription, DialogTitle } from '@radix-ui/react-dialog';
import { ChevronDown, Menu } from 'lucide-react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';

import { LoginButton } from '@/components/auth/Login';
import { Button } from '@/components/ui/Button';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/Drawer';
import { Link } from '@/i18n/routing';

import LanguageDropdown from './LanguageDropdown';
import { ThemeToggle } from './ThemeToggle';

type NavLink = {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
};

export default function Navbar({
  variant = 'marketing',
}: {
  /** Marketing floats under the promo banner. Blog is a full-width sticky bar. */
  variant?: 'marketing' | 'blog';
}) {
  const t = useTranslations('Navbar');
  const locale = useLocale();
  const isArabic = locale === 'ar';
  const navItems = t.raw('navItems') as NavLink[];
  const isBlog = variant === 'blog';

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 8);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={
        isBlog
          ? `fixed inset-x-0 top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-300 ${
              scrolled
                ? 'border-border-soft/80 bg-background/90 shadow-[var(--shadow-card-soft)] backdrop-blur-xl dark:shadow-none'
                : 'bg-background/70 border-transparent backdrop-blur-md'
            }`
          : 'fixed inset-x-0 z-50 flex justify-center md:pt-[20px]'
      }
      style={isBlog ? undefined : { top: 'var(--promo-banner-height, 52px)' }}
    >
      <div
        className={
          isBlog
            ? 'relative mx-auto flex h-16 w-full max-w-[1440px] items-center justify-between px-4 md:h-[72px] md:px-[80px]'
            : `border-divider-soft bg-surface-card relative flex h-[57px] w-full items-center justify-between border-b px-4 shadow-[var(--shadow-card-soft)] transition-all duration-300 md:w-[1360px] md:rounded-[190px] md:border md:px-[28px] dark:shadow-none ${scrolled ? 'backdrop-blur-md' : ''}`
        }
      >
        {/* Logo */}
        <Link href="/" className="flex items-center" aria-label="NexHiro home">
          <Image
            src="/teal-wordmark.png"
            alt="NexHiro"
            width={176}
            height={28}
            priority
            className="h-[22px] w-auto md:h-[24px]"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-[28px] md:flex">
          <div className="flex items-center gap-[32px]">
            {navItems.map((item) =>
              item.children ? (
                <div
                  key={item.href}
                  className="group relative flex h-[57px] items-center"
                >
                  <Link
                    href={item.href}
                    className="font-matter hover:text-brand-primary text-text-primary flex items-center gap-1 text-base font-normal tracking-[-0.01em] transition-colors"
                  >
                    {item.label}
                    <ChevronDown
                      size={15}
                      className="transition-transform duration-200 group-focus-within:rotate-180 group-hover:rotate-180"
                    />
                  </Link>
                  <div className="invisible absolute start-[-24px] top-[48px] z-50 w-[320px] translate-y-2 pt-3 opacity-0 transition-all duration-200 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="border-border-soft bg-surface-card grid gap-1 rounded-2xl border p-2 shadow-[var(--shadow-card-soft)]">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          href={child.href}
                          className="font-matter text-text-primary hover:bg-surface-brand hover:text-brand-primary rounded-xl px-4 py-3 text-sm font-medium transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  className="font-matter hover:text-brand-primary text-text-primary text-base font-normal tracking-[-0.01em] transition-colors"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <LanguageDropdown />

          <ThemeToggle />

          <LoginButton
            variant="default"
            className="brand-gradient-button h-[41px] w-[137px] rounded-[132px] px-[16px] text-sm font-medium"
            value={t('deployHiro')}
          />
        </nav>

        {/* Mobile Navigation */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <LanguageDropdown />
          <Drawer
            open={open}
            onOpenChange={setOpen}
            direction={isArabic ? 'left' : 'right'}
          >
            <DrawerTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label={t('openMenu')}
              >
                <Menu className="text-text-primary size-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="bg-surface-card px-4">
              <DialogTitle className="sr-only">{t('menu')}</DialogTitle>
              <DialogDescription className="sr-only">
                Navigation Menu
              </DialogDescription>
              <div className="flex flex-col gap-4 pt-8">
                {navItems.map((item) => (
                  <div key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className="text-text-primary flex h-12 items-center text-lg font-medium"
                    >
                      {item.label}
                    </Link>
                    {item.children && (
                      <div className="border-divider ms-3 flex flex-col border-s ps-4">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            onClick={() => setOpen(false)}
                            className="text-text-secondary hover:text-brand-primary flex min-h-10 items-center py-2 text-sm transition-colors"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <hr className="border-divider my-8 w-full" />
              <div className="flex flex-col items-center gap-4">
                <LoginButton
                  size="lg"
                  variant="default"
                  className="brand-gradient-button w-full rounded-full font-medium"
                  value={t('deployHiro')}
                />
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </header>
  );
}
